import { convertObjectPropsToLowerCase } from '~/javascripts/lib/convert_utils';
import { BASE_ERROR_DICTIONARY } from '~/javascripts/lib/error_dictionary';
import {
  ERROR_FETCHING_DATA,
  ERROR_PROCESSING_PAYMENT,
  CONTACT_SUPPORT_PLACEHOLDER,
  CONTACT_SUPPORT_LINK,
  CONTACT_US_PATH,
  BILLING_ACCOUNT_PATH,
  ROLES_AND_PERMISSIONS_LINK,
} from '~/javascripts/constants';
import {
  ERROR_FETCHING_NAMESPACES_MESSAGE,
  ERROR_INACTIVE_SUBSCRIPTION_MESSAGE,
} from '~/javascripts/subscription/constants';
import { ERROR_TOO_MANY_ADDON_SEATS_MESSAGE } from '~/javascripts/purchase/constants';

const TRANSACTION_DECLINED_DOCS_LINK =
  'https://docs.gitlab.com/subscriptions/gitlab_com/gitlab_subscription_troubleshooting/#error-transaction_not_allowed';
const TRANSFER_RESTRICTIONS_LINK =
  'https://docs.gitlab.com/subscriptions/manage_subscription/#transfer-restrictions';
const stripe3dsLink = 'https://docs.stripe.com/payments/3d-secure';

// Stripe errors
const AUTHENTICATION_NOT_SUPPORTED_3DS_STRIPE_ERROR_TYPE =
  '[card_error/authentication_required/authentication_required]';
const SETUP_AUTHENTICATION_FAILURE_3DS_STRIPE_ERROR_TYPE =
  '[invalid_request_error/setup_intent_authentication_failure]';
const PAYMENT_AUTHENTICATION_FAILURE_3DS_STRIPE_ERROR_TYPE =
  '[invalid_request_error/payment_intent_authentication_failure]';
const INCORRECT_CVC_NUMBER_STRIPE_ERROR_TYPE = '[card_error/invalid_cvc]';
const INVALID_CVC_NUMBER_STRIPE_ERROR_TYPE = '[card_error/incorrect_cvc]';
const INVALID_CVC_NUMBER_SUBCODE_STRIPE_ERROR_TYPE = '[card_error/incorrect_cvc/incorrect_cvc]';
const INVALID_EXPIRY_MONTH_STRIPE_ERROR_TYPE = '[card_error/invalid_expiry_month]';
const INVALID_EXPIRY_YEAR_STRIPE_ERROR_TYPE = '[card_error/invalid_expiry_year]';
const INCORRECT_NUMBER_STRIPE_ERROR_TYPE = '[card_error/incorrect_number]';
const INCORRECT_NUMBER_SUBCODE_STRIPE_ERROR_TYPE = '[card_error/incorrect_number/incorrect_number]';
const EXPIRED_CARD_STRIPE_ERROR_TYPE = '[card_error/expired_card]';
const EXPIRED_CARD_SUBCODE_STRIPE_ERROR_TYPE = '[card_error/expired_card/expired_card]';
const CARD_DECLINED_GENERIC_STRIPE_ERROR_TYPE = '[card_error/card_declined/generic_decline]';
const CARD_DECLINED_DO_NOT_HONOR_STRIPE_ERROR_TYPE = '[card_error/card_declined/do_not_honor]';
const CARD_DECLINED_INSUFFICIENT_FUNDS_STRIPE_ERROR_TYPE =
  '[card_error/card_declined/insufficient_funds]';
const CARD_DECLINED_INVALID_ACCOUNT_STRIPE_ERROR_TYPE =
  '[card_error/card_declined/invalid_account]';
const CARD_DECLINED_CARD_VELOCITY_EXCEEDED_STRIPE_ERROR_TYPE =
  '[card_error/card_declined/card_velocity_exceeded]';
const CARD_DECLINED_TRY_AGAIN_LATER_STRIPE_ERROR_TYPE =
  '[card_error/card_declined/try_again_later]';
const CARD_NOT_SUPPORTED_STRIPE_ERROR_TYPE = '[card_error/card_declined/card_not_supported]';
const PROCESSING_ERROR_STRIPE_ERROR_TYPE = '[card_error/processing_error]';
const PROCESSING_ERROR_SUBCODE_STRIPE_ERROR_TYPE = '[card_error/processing_error/processing_error]';
const AMOUNT_TOO_LARGE_STRIPE_ERROR_TYPE = '[invalid_request_error/amount_too_large]';
const TRANSACTION_NOT_ALLOWED_STRIPE_ERROR_TYPE =
  '[card_error/card_declined/transaction_not_allowed]';

// Zuora address related errors
export const INVALID_ZIP_CODE_CITY_ERROR_TYPE = '[address_error/invalid_zip_code_city]';
export const INVALID_ZIP_CODE_STATE_ERROR_TYPE = '[address_error/invalid_zip_code_state]';
export const INVALID_STREET_NAME_ERROR_TYPE = '[address_error/invalid_street_name]';
export const INCOMPLETE_OR_INVALID_ADDRESS_ERROR_TYPE =
  '[address_error/incomplete_or_invalid_address]';
export const ADDRESS_NOT_GEOCODED_ERROR_TYPE = '[address_error/address_not_geocoded]';

export const ERROR_CHANGING_NAMESPACE_TYPE = 'error_changing_namespace_type';
export const ERROR_GETTING_NAMESPACE_TYPE = 'error_getting_namespace_type';

export const CLAIM_SUBSCRIPTION_ERROR_TYPE = 'claim_subscription_error_type';
export const ERROR_TOO_MANY_ADDON_SEATS_TYPE = 'error_too_many_addon_seats_type';
export const ERROR_FETCHING_NAMESPACES_TYPE = 'error_fetching_namespaces_type';
export const ERROR_INACTIVE_SUBSCRIPTION_TYPE = 'error_inactive_subscription_type';
export const ERROR_PROCESSING_PAYMENT_TYPE = 'error_processing_payment_type';
export const ERROR_FETCHING_DATA_TYPE = 'error_fetching_data_type';
export const MULTIPLE_INVOICE_ITEMS_ERROR_TYPE = 'MULTIPLE_INVOICE_ITEMS_ERROR';
export const SUBSCRIPTION_MODIFICATION_NOT_ALLOWED_ERROR_TYPE =
  'SUBSCRIPTION_MODIFICATION_NOT_ALLOWED_ERROR';
const FORBIDDEN_ACTION_ADMIN_IMPERSONATING_ERROR_TYPE =
  'FORBIDDEN_ACTION_ADMIN_IMPERSONATING_ERROR_TYPE';

const RECAPTCHA_VALIDATION_FAILED_ERROR_TYPE = '[recaptcha_validation_failed]';

const ERROR_UNAUTHORIZED_NAMESPACE = 'error_unauthorized_namespace';

// Zuora errors
const REQUIRED_FIELD_MISSING_CARD_NUMBER_ZUORA_ERROR_TYPE = '[creditCardNumber:1]';
const REQUIRED_FIELD_MISSING_EXPIRATION_MONTH_ZUORA_ERROR_TYPE = '[creditCardExpirationMonth:1]';
const REQUIRED_FIELD_MISSING_EXPIRATION_YEAR_ZUORA_ERROR_TYPE = '[creditCardExpirationYear:1]';
const REQUIRED_FIELD_MISSING_SECURITY_CODE_ZUORA_ERROR_TYPE = '[cardSecurityCode:1]';
const REQUIRED_FIELD_MISSING_CARDHOLDER_NAME_ZUORA_ERROR_TYPE = '[creditCardHolderName:1]';
const INVALID_CARD_NUMBER_ZUORA_ERROR_TYPE = '[creditCardNumber:2]';
const INVALID_CARD_TYPE_ZUORA_ERROR_TYPE = `[creditCardNumber:3]`;

// Error objects
const REQUIRED_FIELD_MISSING_ZUORA_ERROR_OBJECT_TITLE = 'Required field not completed';
const REQUIRED_FIELD_MISSING_EXPIRATION_DATE_ZUORA_ERROR_OBJECT = {
  title: REQUIRED_FIELD_MISSING_ZUORA_ERROR_OBJECT_TITLE,
  message: `Enter a valid expiration date. If the problem persists, please ${CONTACT_SUPPORT_PLACEHOLDER}.`,
  links: {
    supportLink: CONTACT_SUPPORT_LINK,
  },
};
const INCORRECT_CVV_NUMBER_ERROR_OBJECT = {
  title: "The card's security code is incorrect",
  message: "Check the card's security code before submitting again, or use a different card.",
  links: {},
};
const INVALID_EXPIRY_DATE_ERROR_OBJECT = {
  title: 'The card has expired',
  message: 'Check the expiration date, or use a different card.',
  links: {},
};
const INCORRECT_CARD_NUMBER_ERROR_OBJECT = {
  title: 'The card number is incorrect',
  message: "Check the card's number, or use a different card.",
  links: {},
};
const CARD_DECLINED_ERROR_OBJECT = {
  title: 'The card was declined',
  message:
    'Contact your card issuer for more information on why the card was declined, or try again later with a different card.',
  links: {},
};
const INSUFFICIENT_FUNDS_ERROR_OBJECT = {
  title: 'The card has insufficient funds to complete the purchase',
  message: 'Make sure you have sufficient funds in your account, or use a different card.',
  links: {},
};
const INVALID_ACCOUNT_ERROR_OBJECT = {
  title: 'The card or account connected to the card is invalid',
  message:
    'Contact your card issuer to check that the card is working correctly, or use a different card.',
  links: {},
};
const CARD_VELOCITY_EXCEEDED_ERROR_OBJECT = {
  title: 'The card was declined',
  message:
    'You have exceeded the balance, credit limit, or transaction amount limit available on this card, or made repeated attempts too frequently.',
  links: {},
};
const PROCESSING_ERROR_OBJECT = {
  title: 'An error occurred while processing the card',
  message: 'Try again later, or use a different card.',
  links: {},
};
const CARD_NOT_SUPPORTED_ERROR_OBJECT = {
  title: 'The card does not support this type of purchase',
  message:
    'Contact your card issuer to make sure this card can be used to make this type of purchase, or use a different card. %{additionalInformationLinkStart}Learn more%{additionalInformationLinkEnd} about this error.',
  links: {
    additionalInformationLink:
      'https://support.stripe.com/questions/your-card-does-not-support-this-type-of-purchase-error-when-processing-payment',
  },
};

const INVALID_ZIP_CODE_STATE_ERROR_OBJECT = {
  message: `Address contains an invalid ZIP/postal code and state combination. %{linkStart}Update the address%{linkEnd} to continue with your purchase.`,
  links: {
    link: BILLING_ACCOUNT_PATH,
  },
};

const INVALID_ZIP_CODE_CITY_ERROR_OBJECT = {
  message: `Address contains a city that could not be found or determined from postal code. %{linkStart}Update the address%{linkEnd} to continue with your purchase.`,
  links: {
    link: BILLING_ACCOUNT_PATH,
  },
};

const INVALID_STREET_NAME_ERROR_OBJECT = {
  message: `Address contains an invalid street name. %{linkStart}Update the address%{linkEnd} to continue with your purchase.`,
  links: {
    link: BILLING_ACCOUNT_PATH,
  },
};

const INCOMPLETE_OR_INVALID_ADDRESS_ERROR_OBJECT = {
  message: `Address is incomplete or invalid. %{linkStart}Update the address%{linkEnd} to continue with your purchase.`,
  links: {
    link: BILLING_ACCOUNT_PATH,
  },
};
const ADDRESS_NOT_GEOCODED_ERROR_OBJECT = {
  message: `Address is invalid. %{linkStart}Update the address%{linkEnd} to continue with your purchase.`,
  links: {
    link: BILLING_ACCOUNT_PATH,
  },
};

const AUTHENTICATION_FAILURE_3DS_ERROR_OBJECT = {
  title: '3D Secure authentication failed',
  message:
    '%{stripe3dsLinkStart}3D Secure authentication%{stripe3dsLinkEnd} failed. Please try the credit card again, or %{salesLinkStart}contact our sales team%{salesLinkEnd} to purchase.',
  links: {
    stripe3dsLink,
    salesLink: CONTACT_US_PATH,
  },
};

export const PURCHASE_ERROR_DICTIONARY = convertObjectPropsToLowerCase({
  ...BASE_ERROR_DICTIONARY,
  [ERROR_UNAUTHORIZED_NAMESPACE]: {
    message: `Purchase could not be completed because you are not an Owner of the %{namespaceStart}namespace%{namespaceEnd} namespace. Ask the namespace Owner to purchase or assign you the %{rolesAndPermissionsLinkStart}Owner role%{rolesAndPermissionsLinkEnd} to this namespace.`,
    links: {
      rolesAndPermissionsLink: ROLES_AND_PERMISSIONS_LINK,
    },
  },
  [ERROR_CHANGING_NAMESPACE_TYPE]: {
    message: `Could not change the namespace for the subscription due to %{transferRestrictionsLinkStart}one of these limitations%{transferRestrictionsLinkEnd}. Please ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      transferRestrictionsLink: TRANSFER_RESTRICTIONS_LINK,
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [ERROR_GETTING_NAMESPACE_TYPE]: {
    message: `Could not get namespace info. If the problem persists, ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [ERROR_FETCHING_NAMESPACES_TYPE]: {
    message: ERROR_FETCHING_NAMESPACES_MESSAGE,
  },
  [ERROR_INACTIVE_SUBSCRIPTION_TYPE]: {
    message: ERROR_INACTIVE_SUBSCRIPTION_MESSAGE,
    links: {},
  },
  [MULTIPLE_INVOICE_ITEMS_ERROR_TYPE]: {
    message:
      'Your purchase cannot be completed at this time, because we are still processing your recent subscription charges. You can either %{salesLinkStart}contact the sales team%{salesLinkEnd} for assistance or wait 24 hours and try again.',
    links: {
      salesLink: CONTACT_US_PATH,
    },
  },
  [SUBSCRIPTION_MODIFICATION_NOT_ALLOWED_ERROR_TYPE]: {
    message:
      'You cannot modify this subscription. %{salesLinkStart}Contact the sales team%{salesLinkEnd} for assistance.',
    links: {
      salesLink: CONTACT_US_PATH,
    },
  },
  [ERROR_TOO_MANY_ADDON_SEATS_TYPE]: {
    message: ERROR_TOO_MANY_ADDON_SEATS_MESSAGE,
  },
  [CLAIM_SUBSCRIPTION_ERROR_TYPE]: {
    message: `An error occured while claiming subscription with this coupon code. Please try again. If the problem persists, ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [AUTHENTICATION_NOT_SUPPORTED_3DS_STRIPE_ERROR_TYPE]: {
    title: 'Authentication required for this transaction',
    message:
      '%{stripe3dsLinkStart}3D Secure authentication%{stripe3dsLinkEnd} is not supported. Please %{salesLinkStart}contact our sales team%{salesLinkEnd} to purchase, or try a different credit card.',
    links: {
      stripe3dsLink,
      salesLink: CONTACT_US_PATH,
    },
  },
  [SETUP_AUTHENTICATION_FAILURE_3DS_STRIPE_ERROR_TYPE]: AUTHENTICATION_FAILURE_3DS_ERROR_OBJECT,
  [PAYMENT_AUTHENTICATION_FAILURE_3DS_STRIPE_ERROR_TYPE]: AUTHENTICATION_FAILURE_3DS_ERROR_OBJECT,
  [INCORRECT_CVC_NUMBER_STRIPE_ERROR_TYPE]: INCORRECT_CVV_NUMBER_ERROR_OBJECT,
  [INVALID_CVC_NUMBER_STRIPE_ERROR_TYPE]: INCORRECT_CVV_NUMBER_ERROR_OBJECT,
  [INVALID_CVC_NUMBER_SUBCODE_STRIPE_ERROR_TYPE]: INCORRECT_CVV_NUMBER_ERROR_OBJECT,
  [INVALID_EXPIRY_MONTH_STRIPE_ERROR_TYPE]: INVALID_EXPIRY_DATE_ERROR_OBJECT,
  [INVALID_EXPIRY_YEAR_STRIPE_ERROR_TYPE]: INVALID_EXPIRY_DATE_ERROR_OBJECT,
  [INCORRECT_NUMBER_STRIPE_ERROR_TYPE]: INCORRECT_CARD_NUMBER_ERROR_OBJECT,
  [INCORRECT_NUMBER_SUBCODE_STRIPE_ERROR_TYPE]: INCORRECT_CARD_NUMBER_ERROR_OBJECT,
  [EXPIRED_CARD_STRIPE_ERROR_TYPE]: INVALID_EXPIRY_DATE_ERROR_OBJECT,
  [EXPIRED_CARD_SUBCODE_STRIPE_ERROR_TYPE]: INVALID_EXPIRY_DATE_ERROR_OBJECT,
  [CARD_DECLINED_GENERIC_STRIPE_ERROR_TYPE]: CARD_DECLINED_ERROR_OBJECT,
  [CARD_DECLINED_DO_NOT_HONOR_STRIPE_ERROR_TYPE]: CARD_DECLINED_ERROR_OBJECT,
  [CARD_DECLINED_INSUFFICIENT_FUNDS_STRIPE_ERROR_TYPE]: INSUFFICIENT_FUNDS_ERROR_OBJECT,
  [CARD_DECLINED_INVALID_ACCOUNT_STRIPE_ERROR_TYPE]: INVALID_ACCOUNT_ERROR_OBJECT,
  [CARD_DECLINED_CARD_VELOCITY_EXCEEDED_STRIPE_ERROR_TYPE]: CARD_VELOCITY_EXCEEDED_ERROR_OBJECT,
  [CARD_DECLINED_TRY_AGAIN_LATER_STRIPE_ERROR_TYPE]: PROCESSING_ERROR_OBJECT,
  [CARD_NOT_SUPPORTED_STRIPE_ERROR_TYPE]: CARD_NOT_SUPPORTED_ERROR_OBJECT,
  [PROCESSING_ERROR_STRIPE_ERROR_TYPE]: PROCESSING_ERROR_OBJECT,
  [PROCESSING_ERROR_SUBCODE_STRIPE_ERROR_TYPE]: PROCESSING_ERROR_OBJECT,
  [AMOUNT_TOO_LARGE_STRIPE_ERROR_TYPE]: {
    title: 'Amount too large',
    message:
      'We cannot process a payment of this size. Please %{salesLinkStart}contact our sales team%{salesLinkEnd} to make this purchase.',
    links: {
      salesLink: CONTACT_US_PATH,
    },
  },
  [TRANSACTION_NOT_ALLOWED_STRIPE_ERROR_TYPE]: {
    title: 'Transaction declined',
    message: `Your card issuer might have restricted this transaction as a security measure to protect your account. %{transactionDeclinedLinkStart}Learn how to resolve this issue%{transactionDeclinedLinkEnd} or ${CONTACT_SUPPORT_PLACEHOLDER} if the problem persists.`,
    links: {
      transactionDeclinedLink: TRANSACTION_DECLINED_DOCS_LINK,
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [RECAPTCHA_VALIDATION_FAILED_ERROR_TYPE]: {
    title: 'CAPTCHA verification failed',
    message: 'Submit CAPTCHA challenge again.',
    links: {},
  },
  [ERROR_PROCESSING_PAYMENT_TYPE]: {
    message: ERROR_PROCESSING_PAYMENT,
    links: {
      link: CONTACT_SUPPORT_LINK,
    },
  },
  [INVALID_CARD_NUMBER_ZUORA_ERROR_TYPE]: {
    title: 'Invalid card number',
    message:
      'This card number was not accepted. Please try a different card and submit the form again.',
  },
  [INVALID_CARD_TYPE_ZUORA_ERROR_TYPE]: {
    title: 'Card provider not accepted',
    message:
      'The provider of the card you entered is not accepted. Please try again with a different card.',
  },
  [REQUIRED_FIELD_MISSING_CARD_NUMBER_ZUORA_ERROR_TYPE]: {
    title: REQUIRED_FIELD_MISSING_ZUORA_ERROR_OBJECT_TITLE,
    message: `Enter a valid card number. If the problem persists, please ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [REQUIRED_FIELD_MISSING_EXPIRATION_MONTH_ZUORA_ERROR_TYPE]:
    REQUIRED_FIELD_MISSING_EXPIRATION_DATE_ZUORA_ERROR_OBJECT,
  [REQUIRED_FIELD_MISSING_EXPIRATION_YEAR_ZUORA_ERROR_TYPE]:
    REQUIRED_FIELD_MISSING_EXPIRATION_DATE_ZUORA_ERROR_OBJECT,
  [REQUIRED_FIELD_MISSING_SECURITY_CODE_ZUORA_ERROR_TYPE]: {
    title: REQUIRED_FIELD_MISSING_ZUORA_ERROR_OBJECT_TITLE,
    message: `Enter a valid CVV number. If the problem persists, please ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [REQUIRED_FIELD_MISSING_CARDHOLDER_NAME_ZUORA_ERROR_TYPE]: {
    title: REQUIRED_FIELD_MISSING_ZUORA_ERROR_OBJECT_TITLE,
    message: `Enter a valid cardholder name. If the problem persists, please ${CONTACT_SUPPORT_PLACEHOLDER}.`,
    links: {
      supportLink: CONTACT_SUPPORT_LINK,
    },
  },
  [ERROR_FETCHING_DATA_TYPE]: {
    message: ERROR_FETCHING_DATA,
    links: {
      link: CONTACT_SUPPORT_LINK,
    },
  },
  [FORBIDDEN_ACTION_ADMIN_IMPERSONATING_ERROR_TYPE]: {
    message:
      'You are currently logged in as an admin. Please log out or log in as a customer first.',
  },
  [INVALID_ZIP_CODE_CITY_ERROR_TYPE]: INVALID_ZIP_CODE_CITY_ERROR_OBJECT,
  [INVALID_ZIP_CODE_STATE_ERROR_TYPE]: INVALID_ZIP_CODE_STATE_ERROR_OBJECT,
  [INVALID_STREET_NAME_ERROR_TYPE]: INVALID_STREET_NAME_ERROR_OBJECT,
  [INCOMPLETE_OR_INVALID_ADDRESS_ERROR_TYPE]: INCOMPLETE_OR_INVALID_ADDRESS_ERROR_OBJECT,
  [ADDRESS_NOT_GEOCODED_ERROR_TYPE]: ADDRESS_NOT_GEOCODED_ERROR_OBJECT,
});
